import { createContext, useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import CryptoJS from "crypto-js";
import { env } from "../env";
import { isTokenExpired, BEARER_TOKEN_KEY, RPC_TOKEN_KEY, LEGACY_TOKEN_KEY } from "../utils/token";
import { getBearerToken, getRpcToken, clearAuthData } from "../api/apiService";
import { getProfile } from "../api/userApi";
import { refreshAiChatConfig, clearAiChatConfig } from "../api/postgre/aiChatConfigApi";

export const AuthContext = createContext({
    isAuthenticated: false,
    user: null,
    isAuthChecked: false,
    login: () => {},
    setAuthUser: () => {},
    logout: () => {},
});

const getEncryptedUser = () => localStorage.getItem("userInfo") || sessionStorage.getItem("userInfo");
const getRememberMe = () => localStorage.getItem("rememberMe") === "true";


const encryptData = (data, secretKey) => {
    if (!secretKey) {
        return JSON.stringify(data);
    }

    return CryptoJS.AES.encrypt(JSON.stringify(data), secretKey).toString();
};

const decryptData = (encrypted, secretKey) => {
    if (!encrypted) return null;

    if (!secretKey) {
        try {
            return JSON.parse(encrypted);
        } catch {
            return null;
        }
    }

    try {
        const bytes = CryptoJS.AES.decrypt(encrypted, secretKey);
        const decrypted = bytes.toString(CryptoJS.enc.Utf8);
        return decrypted ? JSON.parse(decrypted) : null;
    } catch {
        return null;
    }
};

const persistEncryptedUser = (encryptedData) => {
    const hasLocalToken = Boolean(localStorage.getItem(BEARER_TOKEN_KEY));
    const hasSessionToken = Boolean(sessionStorage.getItem(BEARER_TOKEN_KEY));

    if (hasLocalToken && !hasSessionToken) {
        localStorage.setItem("userInfo", encryptedData);
        sessionStorage.removeItem("userInfo");
        return;
    }

    if (hasSessionToken) {
        sessionStorage.setItem("userInfo", encryptedData);
        localStorage.removeItem("userInfo");
        return;
    }

    if (getRememberMe()) {
        localStorage.setItem("userInfo", encryptedData);
        sessionStorage.removeItem("userInfo");
        return;
    }

    sessionStorage.setItem("userInfo", encryptedData);
    localStorage.removeItem("userInfo");
};

const AuthContextProvider = ({ children }) => {
    const navigate = useNavigate();
    const [isAuthenticated, setIsAuthenticated] = useState(false);
    const [user, setUser] = useState(null);
    const [isAuthChecked, setIsAuthChecked] = useState(false);
    const secretKey = env.REACT_APP_SECRET_KEY || "";

    const logout = useCallback(() => {
        clearAuthData();
        clearAiChatConfig();
        setIsAuthenticated(false);
        setUser(null);
        navigate("/");
    }, [navigate]);

    useEffect(() => {
        const bearerToken = getBearerToken();
        const encryptedUser = getEncryptedUser();

        if (!bearerToken || isTokenExpired(bearerToken, { expireOnError: false })) {
            clearAuthData();
            setIsAuthenticated(false);
            setUser(null);
            setIsAuthChecked(true);
            return;
        }

        const parsedUser = decryptData(encryptedUser, secretKey);
        const rpcToken = getRpcToken();

        if (!rpcToken || !parsedUser || !parsedUser.Email || !parsedUser.id) {
            clearAuthData();
            setIsAuthenticated(false);
            setUser(null);
            setIsAuthChecked(true);
            return;
        }

        setIsAuthenticated(true);
        setUser(parsedUser);
        setIsAuthChecked(true);

        // Resolve/refresh the AI chat config in the background so it's cached
        // and ready before Aura is opened, instead of each request awaiting it
        refreshAiChatConfig().catch(() => {});

        // Refresh profile from API in background so user data stays up to date
        let mounted = true;
        getProfile()
            .then((freshUser) => {
                if (mounted && freshUser) {
                    const encryptedData = encryptData(freshUser, secretKey);
                    persistEncryptedUser(encryptedData);
                    setUser(freshUser);
                }
            })
            .catch((err) => {
                const status = err?.response?.status;
                if (status === 401 || status === 403 || status === 404) {
                    clearAuthData();
                    setIsAuthenticated(false);
                    setUser(null);
                }
                // other errors: silently fall back to stored data
            });

        return () => {
            mounted = false;
        };
    }, [secretKey]);

    // Supports both signatures:
    // 1) login({ userData, bearerToken, token, rememberMe })
    // 2) login(userData, bearerToken, rememberMe, token) // legacy-style
    const setAuthUser = useCallback((nextUser) => {
        const normalizedUser = nextUser || null;
        const encryptedData = encryptData(normalizedUser || {}, secretKey);
        persistEncryptedUser(encryptedData);
        setUser(normalizedUser);
    }, [secretKey]);

    const login = useCallback((arg1, arg2, arg3, arg4, arg5) => {
        const isObjectSignature = arg1 && typeof arg1 === "object" && Object.prototype.hasOwnProperty.call(arg1, "userData");
        const payload = isObjectSignature
            ? arg1
            : { userData: arg1, bearerToken: arg2, rememberMe: arg3, token: arg4, skipNavigate: Boolean(arg5) };

        const { userData, bearerToken, token, rememberMe, skipNavigate = false } = payload;
        const nextBearerToken = bearerToken || token;
        const nextRpcToken = token;
        if (!nextBearerToken) {
            return;
        }

        const encryptedData = encryptData(userData || {}, secretKey);
        const useLocalStorage = rememberMe || getRememberMe();

        if (useLocalStorage) {
            localStorage.setItem("userInfo", encryptedData);
            localStorage.setItem(BEARER_TOKEN_KEY, nextBearerToken);
            if (nextRpcToken) {
                localStorage.setItem(RPC_TOKEN_KEY, nextRpcToken);
            } else {
                localStorage.removeItem(RPC_TOKEN_KEY);
            }
            localStorage.setItem("rememberMe", "true");
            sessionStorage.removeItem("userInfo");
            sessionStorage.removeItem(BEARER_TOKEN_KEY);
            sessionStorage.removeItem(RPC_TOKEN_KEY);
        } else {
            sessionStorage.setItem("userInfo", encryptedData);
            sessionStorage.setItem(BEARER_TOKEN_KEY, nextBearerToken);
            if (nextRpcToken) {
                sessionStorage.setItem(RPC_TOKEN_KEY, nextRpcToken);
            } else {
                sessionStorage.removeItem(RPC_TOKEN_KEY);
            }
            localStorage.removeItem("rememberMe");
            localStorage.removeItem("userInfo");
            localStorage.removeItem(BEARER_TOKEN_KEY);
            localStorage.removeItem(RPC_TOKEN_KEY);
        }

        localStorage.removeItem(LEGACY_TOKEN_KEY);
        sessionStorage.removeItem(LEGACY_TOKEN_KEY);

        setIsAuthenticated(true);
        setUser(userData || null);
        refreshAiChatConfig().catch(() => {});
        if (!skipNavigate) {
            navigate("/dashboard");
        }
    }, [navigate, secretKey]);

    const value = useMemo(
        () => ({
            isAuthenticated,
            user,
            isAuthChecked,
            login,
            setAuthUser,
            logout,
        }),
        [isAuthenticated, isAuthChecked, user, login, setAuthUser, logout]
    );

    if (!isAuthChecked) {
        return null;
    }

    return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export { AuthContextProvider };
